import React from 'react';
import { Info, Plane, MapPin, Clock, Shield, Globe, AlertTriangle, Wallet } from 'lucide-react';

/**
 * Компонент с информацией о воздушном транспорте России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о воздушном транспорте России
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-blue-500" />
          Авиационная система России
        </h3>
        <p>
          <strong>Воздушный транспорт России</strong> играет ключевую роль в обеспечении связности огромной территории страны, простирающейся на 11 часовых поясов. Для многих удаленных регионов авиация является единственным круглогодичным видом транспорта, обеспечивающим стабильное сообщение с центральными регионами.
        </p>
        <p>
          Российская Федерация обладает одной из крупнейших внутренних авиационных сетей в мире, соединяющей более 250 аэропортов. Московский авиационный узел, включающий аэропорты Шереметьево, Домодедово и Внуково, является главным транспортным хабом страны, через который проходит значительная часть пассажиропотока.
        </p>

        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-500" />
          Крупнейшие аэропорты и авиахабы
        </h3>
        <ul>
          <li><strong>Москва</strong> (Шереметьево, Домодедово, Внуково) — крупнейший авиационный узел России.</li>
          <li><strong>Санкт-Петербург</strong> (Пулково) — второй по значимости аэропорт, обслуживающий северо-западный регион.</li>
          <li><strong>Екатеринбург</strong> (Кольцово) — ключевой хаб Урала.</li>
          <li><strong>Новосибирск</strong> (Толмачёво) — важнейший транспортный узел Сибири.</li>
          <li><strong>Владивосток</strong> (Кневичи) — главные воздушные ворота Дальнего Востока.</li>
          <li><strong>Сочи</strong> — крупнейший аэропорт курортной зоны Чёрного моря.</li>
          <li><strong>Красноярск</strong> (Емельяново) — развивающийся хаб в Центральной Сибири.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-500" />
          Время в полете: основные маршруты
        </h3>
        <p>
          Протяженность России с запада на восток определяет существенные различия во времени полета между разными городами:
        </p>
        <ul>
          <li><strong>Москва — Санкт-Петербург:</strong> 1,5 часа (650 км)</li>
          <li><strong>Москва — Сочи:</strong> 2,5 часа (1,360 км)</li>
          <li><strong>Москва — Екатеринбург:</strong> 2,5 часа (1,420 км)</li>
          <li><strong>Москва — Новосибирск:</strong> 4 часа (2,800 км)</li>
          <li><strong>Москва — Красноярск:</strong> 4,5 часа (3,360 км)</li>
          <li><strong>Москва — Иркутск:</strong> 5,5 часов (4,200 км)</li>
          <li><strong>Москва — Якутск:</strong> 6,5 часов (4,900 км)</li>
          <li><strong>Москва — Хабаровск:</strong> 8 часов (6,140 км)</li>
          <li><strong>Москва — Владивосток:</strong> 9 часов (6,430 км)</li>
          <li><strong>Москва — Петропавловск-Камчатский:</strong> 9,5 часов (6,780 км)</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Интересный факт</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Самый длинный регулярный внутренний рейс в России — Москва — Петропавловск-Камчатский, 
            преодолевает 6,780 км за примерно 9,5 часов. Это эквивалентно перелету из Лондона в Нью-Йорк!
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Российские авиаперевозчики
        </h3>
        <p>
          На российском рынке авиаперевозок работает более 100 авиакомпаний, но основной пассажиропоток обслуживают несколько крупных перевозчиков:
        </p>
        <ul>
          <li>
            <strong>Аэрофлот</strong> — национальный авиаперевозчик, член альянса SkyTeam, с крупнейшей маршрутной сетью и современным флотом.
          </li>
          <li>
            <strong>S7 Airlines</strong> — второй по величине перевозчик с хабами в Москве (Домодедово) и Новосибирске.
          </li>
          <li>
            <strong>Уральские авиалинии</strong> — базируется в Екатеринбурге, соединяет Урал с другими регионами России и странами СНГ.
          </li>
          <li>
            <strong>Победа</strong> — лоукост-авиакомпания, предлагающая доступные билеты на популярных направлениях.
          </li>
          <li>
            <strong>UTair</strong> — один из крупнейших вертолетных операторов в мире и пассажирский авиаперевозчик.
          </li>
          <li>
            <strong>Россия</strong> — дочерняя компания Аэрофлота, выполняющая социально значимые рейсы, включая Дальний Восток.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при планировании полета
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Проверьте наличие багажа в вашем тарифе, особенно при полетах лоукостерами</li>
            <li>Учитывайте разницу в часовых поясах при планировании стыковок и встреч</li>
            <li>В зимний период возможны задержки рейсов из-за погодных условий</li>
            <li>Для некоторых удаленных регионов частота рейсов может быть ограничена</li>
            <li>При перелетах на Дальний Восток и в Сибирь учитывайте фактор усталости от длительного перелета</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Часовые пояса и планирование перелетов
        </h3>
        <p>
          Россия охватывает 11 часовых поясов от Калининграда (UTC+2) до Камчатки (UTC+12). 
          Это создает уникальные особенности при планировании дальних перелетов:
        </p>
        <ul>
          <li>При перелетах с запада на восток "теряется" время — прибытие может быть на следующий день.</li>
          <li>При перелетах с востока на запад можно прибыть в пункт назначения "раньше" по местному времени, чем вылетели.</li>
          <li>Разница между Москвой и Владивостоком составляет 7 часов.</li>
          <li>Самая большая разница между регионами России — 10 часов (Калининград и Камчатка).</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-blue-500" />
          Стоимость авиабилетов
        </h3>
        <p>
          Цены на авиабилеты в России зависят от многих факторов:
        </p>
        <ul>
          <li><strong>Сезонность</strong> — летом и в период новогодних праздников цены значительно выше.</li>
          <li><strong>Направление</strong> — популярные курортные направления и бизнес-маршруты обычно дороже.</li>
          <li><strong>Время до вылета</strong> — раннее бронирование (за 2-3 месяца) обычно позволяет сэкономить.</li>
          <li><strong>Субсидированные рейсы</strong> — государство субсидирует ряд социально значимых маршрутов, особенно на Дальний Восток, что делает их более доступными для определенных категорий граждан.</li>
          <li><strong>Программы лояльности</strong> — накопление миль позволяет получать бесплатные билеты или апгрейды класса обслуживания.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам рассчитать время в полете и спланировать оптимальный маршрут путешествия по России и за её пределы!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;